<script setup lang="ts">
  /**
   * Application menu — 03-components/05a-application-header · OSCloud DS 1.1.27
   */
  import type { RouteLocationRaw } from 'vue-router'

  import BfSwitch from './BfSwitch.vue'
  import type {
    BfApplicationMenuGroup,
    BfApplicationMenuItem,
    BfApplicationMenuNotification,
    BfApplicationMenuUser,
  } from '@/typescript/types/ui-components/BfApplicationMenuI'

  withDefaults(
    defineProps<{
      open?: boolean
      /** Identity header. Omit on a menu that is not an account menu. */
      user?: BfApplicationMenuUser | null
      groups?: BfApplicationMenuGroup[]
      notifications?: BfApplicationMenuNotification[]
      /** Caption above the notification rows. */
      notificationsLabel?: string
    }>(),
    { open: false, user: null, groups: () => [], notifications: () => [], notificationsLabel: '' },
  )

  const emit = defineEmits<{
    select: [item: BfApplicationMenuItem]
    toggle: [item: BfApplicationMenuItem, value: boolean]
    /**
     * Something with a destination was activated — a menu row or a notification's action. It
     * navigates itself, so the only thing left for the consumer to do is close the menu behind
     * it. Separate from `select`, which asks the consumer to *perform* the row's action, and
     * payload-free because closing does not depend on which row it was.
     */
    navigate: []
  }>()

  const isLink = (item: BfApplicationMenuItem): boolean => Boolean(item.to || item.href)

  const onItemActivate = (item: BfApplicationMenuItem): void => {
    if (isLink(item)) {
      emit('navigate')
      return
    }
    emit('select', item)
  }

  /**
   * The destination attributes, one branch each — `href` is never handed to RouterLink.
   *
   * Vue merges fallthrough attrs AFTER the component's own rendered attrs, so a `:href` bound
   * alongside `:to` overrides the href RouterLink has just computed; bound to `undefined`, it
   * strips the attribute altogether. An <a> with no href cannot be opened in a new tab, copied,
   * middle-clicked or reached by keyboard, and is not announced as a link — the plain click keeps
   * working, which is what hides it.
   */
  const linkAttrs = (target: { to?: RouteLocationRaw; href?: string }): Record<string, unknown> => {
    if (target.to) return { to: target.to }
    return target.href ? { href: target.href } : {}
  }
</script>

<template>
  <div class="application-menu" :data-state="open ? 'open' : undefined">
    <div v-if="user" class="application-menu__header">
      <span v-if="user.initials" class="application-menu__avatar" aria-hidden="true">{{ user.initials }}</span>
      <div class="application-menu__identity">
        <div class="application-menu__name">{{ user.name }}</div>
        <div v-if="user.email" class="application-menu__email">{{ user.email }}</div>
      </div>
    </div>

    <template v-if="notifications.length">
      <span v-if="notificationsLabel" class="application-menu__group-label">{{ notificationsLabel }}</span>
      <div
        v-for="(notification, index) in notifications"
        :key="`notification-${index}`"
        class="application-menu__notification"
        :data-unread="notification.unread ? '' : undefined"
      >
        <span class="application-menu__notification-dot" aria-hidden="true" />
        <span class="application-menu__notification-body">
          <span class="application-menu__notification-text">{{ notification.text }}</span>
          <span v-if="notification.time" class="application-menu__notification-time">{{ notification.time }}</span>
          <component
            :is="notification.action?.to ? 'RouterLink' : 'a'"
            v-if="notification.action"
            class="application-menu__notification-action"
            v-bind="linkAttrs(notification.action)"
            @click="emit('navigate')"
          >
            {{ notification.action.label }}
          </component>
        </span>
      </div>
    </template>

    <template v-for="(group, groupIndex) in groups" :key="`group-${groupIndex}`">
      <div v-if="group.divided" class="application-menu__divider" role="separator" />
      <span v-if="group.label" class="application-menu__group-label">{{ group.label }}</span>
      <ul class="application-menu__list" role="list">
        <li v-for="item in group.items" :key="item.label">
          <!-- A row that carries a setting: the switch IS the control, so the row is BfSwitch's own
               <label> and the text toggles it. Not a button — there is nothing to activate. -->
          <div v-if="item.toggle" class="application-menu__item application-menu__item--toggle">
            <component :is="item.icon" v-if="item.icon" class="application-menu__icon" :size="16" />
            <BfSwitch
              :model-value="item.toggle.modelValue"
              :label="item.label"
              control-trailing
              @update:modelValue="emit('toggle', item, $event)"
            />
          </div>

          <component
            :is="item.to ? 'RouterLink' : item.href ? 'a' : 'button'"
            v-else
            class="application-menu__item"
            :class="{ 'application-menu__item--destructive': item.destructive }"
            v-bind="linkAttrs(item)"
            :type="isLink(item) ? undefined : 'button'"
            @click="onItemActivate(item)"
          >
            <component :is="item.icon" v-if="item.icon" class="application-menu__icon" :size="16" />
            {{ item.label }}
          </component>
        </li>
      </ul>
    </template>

    <slot />
  </div>
</template>

<style scoped lang="scss">
  .application-menu {
    --application-menu-row-padding-vertical: 12px; /* the card's declared exception to --bf-sp-* */
    --application-menu-row-padding-horizontal: 14px; /* the card's declared exception to --bf-sp-* */
    position: absolute;
    top: calc(100% + var(--bf-sp-1));
    right: 0;
    z-index: 40;
    width: max-content;
    min-width: 280px;
    max-width: 360px;
    padding: var(--bf-sp-1) 0;
    background: var(--bf-white);
    border: 1px solid var(--bf-line-2);
    border-radius: var(--bf-r-lg);
    box-shadow: var(--bf-shadow-menu);
    font-family: var(--bf-font-body);
    text-align: left;
    visibility: hidden;
    opacity: 0;
    transform: translateY(-4px);
    pointer-events: none;
    transition:
      opacity var(--bf-dur-fast) var(--bf-ease),
      transform var(--bf-dur-fast) var(--bf-ease),
      visibility var(--bf-dur-fast);

    &[data-state='open'] {
      visibility: visible;
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }

    &__header {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      padding: var(--application-menu-row-padding-vertical) var(--application-menu-row-padding-horizontal);
      border-bottom: 1px solid var(--bf-line-2);
    }

    &__avatar {
      flex: 0 0 auto;
      width: 36px;
      height: 36px;
      display: grid;
      place-items: center;
      border-radius: var(--bf-r-pill);
      background: linear-gradient(135deg, var(--bf-orange-light), var(--bf-orange-dark));
      color: var(--bf-white);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs_5);
      font-weight: var(--bf-fw-semibold);
      letter-spacing: var(--bf-tr-nudge);
    }

    &__identity {
      min-width: 0;
    }

    &__name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--bf-fs-xs_5);
      font-weight: var(--bf-fw-semibold);
      color: var(--bf-fg-1);
    }

    &__email {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--bf-fs-xs);
      color: var(--bf-fg-3);
    }

    &__group-label {
      display: block;
      padding: var(--bf-sp-2) var(--application-menu-row-padding-horizontal) var(--bf-sp-1);
      font-size: var(--bf-fs-2xs);
      font-weight: var(--bf-fw-semibold);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-caps-sm);
      color: var(--bf-fg-3);
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      width: 100%;
      padding: var(--application-menu-row-padding-vertical) var(--application-menu-row-padding-horizontal);
      border: 0;
      background: none;
      font: inherit;
      font-size: var(--bf-fs-xs_5);
      line-height: var(--bf-lh-snug);
      color: var(--bf-fg-2);
      text-decoration: none;
      text-align: left;
      cursor: pointer;
      transition:
        background var(--bf-dur-fast) var(--bf-ease),
        color var(--bf-dur-fast) var(--bf-ease);

      &:hover,
      &:focus-visible {
        background: var(--bf-nav-active);
        color: var(--bf-orange-dark);
      }

      &:focus-visible {
        outline: 2px solid var(--bf-orange-dark);
        outline-offset: -2px;
      }

      &--destructive {
        color: var(--bf-error-accent);

        &:hover,
        &:focus-visible {
          background: var(--bf-error-bg);
          color: var(--bf-error-accent);
        }
      }
    }

    &__icon {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
      color: currentColor;
    }

    /* The switch fills the row so its track lands on the same trailing edge the other rows' text
       ends at; the row keeps the item padding and takes no hover, since nothing navigates. */
    &__item--toggle {
      cursor: default;

      .switch {
        width: 100%;
        justify-content: space-between;
      }
    }

    &__divider {
      height: 1px;
      margin: var(--bf-sp-1) 0;
      background: var(--bf-line);
    }

    &__notification {
      display: grid;
      grid-template-columns: 6px 1fr;
      column-gap: var(--bf-sp-2);
      align-items: start;
      padding: var(--application-menu-row-padding-vertical) var(--application-menu-row-padding-horizontal);
      color: inherit;
      text-decoration: none;
      transition: background var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        background: var(--bf-menu-hover);
      }

      &[data-unread] .application-menu__notification-dot {
        background: var(--bf-orange-dark);
      }

      &[data-unread] .application-menu__notification-text {
        color: var(--bf-fg-1);
        font-weight: var(--bf-fw-semibold);
      }
    }

    &__notification-body {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
    }

    &__notification-dot {
      width: 6px;
      height: 6px;
      margin-top: 6px;
      border-radius: var(--bf-r-pill);
      background: transparent;
    }

    &__notification-text {
      display: block;
      font-size: var(--bf-fs-xs_5);
      line-height: var(--bf-lh-snug);
      color: var(--bf-fg-2);
    }

    &__notification-time {
      display: block;
      margin-top: var(--bf-sp-1);
      font-size: var(--bf-fs-2xs);
      color: var(--bf-fg-3);
    }

    &__notification-action {
      display: inline-flex;
      align-items: center;
      margin-top: var(--bf-sp-2);
      padding: var(--bf-sp-1) var(--bf-sp-2);
      border: 0;
      border-radius: var(--bf-r-sm);
      background: var(--bf-orange-dark);
      color: var(--bf-white);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: 1.4;
      text-decoration: none;
      cursor: pointer;
      transition: background var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        background: var(--bf-orange-hover);
      }

      &:focus-visible {
        outline: 2px solid var(--bf-orange-dark);
        outline-offset: 2px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .application-menu {
      transition: none;
    }
  }
</style>
